import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import {
  ChartEmptyState,
  ChartErrorState,
  ChartLoading,
  TimeComparisonLineChart,
} from "@/shared/charts";
import type { TimeComparisonLineChartConfig } from "@/shared/charts";
import type { SiteTotalPopulationChart } from "@/features/deployment-dashboard/live-view/hooks/use-site-total-population-chart";

interface PopulationChartViewProps {
  chart: SiteTotalPopulationChart | undefined;
  isLoading: boolean;
  error: Error | null;
  hasGrant: boolean;
}

const POPULATION_CHART_CONFIG: TimeComparisonLineChartConfig = {
  currentSeriesLabel: "Current Total Population",
  comparisonSeriesLabel: "4-Week Average",
  nowLabel: "Now",
  ariaDescription: "Exact flow-derived Total Population over time",
  thresholds: [
    { value: 100, label: "" },
    { value: 450, label: "" },
  ],
  valueFormatter: (value: number) => Math.round(value).toLocaleString(),
};

function describeWarning(chart: SiteTotalPopulationChart, hasError: boolean): string | null {
  if (hasError) {
    return "Unable to refresh the Total Population chart. Showing the last known data.";
  }
  if (chart.dataStatus === "partial") {
    return "Showing partial data: not every selected area has configured Total Population data.";
  }
  return null;
}

/** Places Now on the latest plotted population sample, inside the chart viewport. */
function calculateNowTimestamp(chart: SiteTotalPopulationChart): number {
  const latestTimestamp = chart.currentData.at(-1)?.timestamp ?? chart.snapshotAt;
  return Math.min(Math.max(latestTimestamp, chart.rangeStart), chart.rangeEnd);
}

export function PopulationChartView({
  chart,
  isLoading,
  error,
  hasGrant,
}: PopulationChartViewProps) {
  if (!hasGrant) {
    return (
      <ChartEmptyState
        title="Chart View unavailable"
        description="You do not have permission to view population analytics."
      />
    );
  }

  if (isLoading) {
    return <ChartLoading label="Loading Total Population chart" />;
  }

  if (chart === undefined) {
    return error !== null ? (
      <ChartErrorState message="Unable to load the Total Population chart." />
    ) : (
      <ChartEmptyState title="No Total Population data" />
    );
  }

  if (chart.dataStatus === "unavailable") {
    return (
      <ChartEmptyState
        title="No Total Population data"
        description="No selected areas currently have flow-derived Total Population data."
      />
    );
  }

  const warning = describeWarning(chart, error !== null);
  const nowTimestamp = calculateNowTimestamp(chart);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {warning !== null && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
          {warning}
        </Alert>
      )}
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        <TimeComparisonLineChart
          currentData={chart.currentData}
          comparisonData={[]}
          now={nowTimestamp}
          rangeStart={chart.rangeStart}
          rangeEnd={chart.rangeEnd}
          height="100%"
          config={POPULATION_CHART_CONFIG}
        />
      </Box>
    </Box>
  );
}
